import type { ComponentProps, ReactNode } from "react";

import { cx } from "./cx";

export interface DetailsPanelProps extends Omit<ComponentProps<"details">, "title"> {
  /**
   * `why` is the rationale a screen owes but should not lead with; `caveats` is the result
   * screen's "what not to over-read". Same idiom, slightly different register.
   */
  variant?: "why" | "caveats";
  summary: ReactNode;
  /** Sits inside the summary, after the text -- the caveat count does this. */
  summaryAside?: ReactNode;
}

const SHELL = "border-t border-line pt-panel";

/**
 * Collapsed by default, and that is the point: the rationale is still here and still
 * measured, it is just no longer the first thing between somebody and the button they came
 * to press. There for whoever wants it, in front of nobody.
 */
export function DetailsPanel({
  variant = "why",
  summary,
  summaryAside,
  className,
  children,
  ...rest
}: DetailsPanelProps) {
  const why = variant === "why";
  return (
    <details
      className={cx(
        SHELL,
        why
          ? "mt-panel-t [&>p]:mt-stack [&>p]:mb-0 [&>p]:text-detail [&>p]:text-ink-soft"
          : "mt-6 [&_ul]:pl-pad [&_ul]:text-detail [&_ul]:text-ink-soft [&_li]:my-item",
        className
      )}
      {...rest}
    >
      <summary
        className={cx(
          "cursor-pointer font-semibold",
          why ? "text-note text-ink-soft" : "text-caveat"
        )}
      >
        {summary}
        {summaryAside === undefined ? null : (
          <span className="text-note text-ink-soft"> {summaryAside}</span>
        )}
      </summary>
      {children}
    </details>
  );
}
